import {
  CommonModule,
  NgIf
} from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  EventEmitter,
  Inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  TemplateRef
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  ButtonDirective,
  FormControlDirective,
  FormDirective,
  FormFeedbackComponent,
  FormFloatingDirective,
  FormLabelDirective,
  InputGroupComponent,
  InputGroupTextDirective
} from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { ServerAuthUrlPipe } from 'auth';
import { TranslateDirective, TranslatePipe, TranslateService } from 'i18n';
import { ImageLink, Media, MediaEntity, NULL_MEDIA } from 'media';
import { asyncScheduler, combineLatest, debounceTime, distinctUntilChanged, filter, map, of, partition, Subject, Subscription, switchMap, tap } from 'rxjs';
import { FileSizePipe, FormChangeDetector, Logger, Platform, SafeUrlPipe, ToastService } from 'shared';

type FormState = {
  valid: boolean;
  dirty: boolean;
}

type MediaForm = {
  cover: FormControl<ImageLink | null>;
  name: FormControl<string>;
  description: FormControl<string>;
  language: FormControl<string>;
};

@Component({
    selector: 'media-detail',
    standalone: true,
    templateUrl: './media-detail.component.html',
    styleUrl: './media-detail.component.scss',
    imports: [
      ReactiveFormsModule,
      CommonModule,
      ButtonDirective,
      FormControlDirective,
      FormLabelDirective,
      FormDirective,
      FormFloatingDirective,
      FormFeedbackComponent,
      InputGroupComponent,
      InputGroupTextDirective,
      IconModule,
      NgIf,
      FileSizePipe,
      ServerAuthUrlPipe,
      SafeUrlPipe,
      TranslateDirective,
      TranslatePipe
    ],
    exportAs: "mediaDetail",
    changeDetection: ChangeDetectionStrategy.OnPush
  })
  export class MediaDetailComponent implements OnInit, OnDestroy {
    media$ = input<Media | null | undefined>();
    editMode$ = input<boolean>();
    
    controlsTemplate = contentChild<TemplateRef<any>>(TemplateRef);

    @Output() save = new EventEmitter<Partial<Omit<Media, 'id' | 'type'>> & MediaEntity>();
    @Output() delete = new EventEmitter<string>();
    @Output() cancel = new EventEmitter<void>();
    @Output() formState = new EventEmitter<FormState>();

    value$ = signal<Partial<Media> | null>(null);
    mediaId$ = computed(() => this.media$()?.id);

    form = signal<FormGroup<MediaForm> | null>(null);
    formState$ = signal<FormState>({ valid: true, dirty: false });
    protected formChangeSubscription: Subscription | null = null;
    protected formChangeReset: Subject<void> | null = null;
  
    file: File | null = null;
    uploading = signal(false);
    uploadProgress$ = signal<number>(0);
    uploadError = false;
    isDragging = false;

    protected logger = new Logger('MediaDetailComponent');
  
    constructor(
      private fb: NonNullableFormBuilder,
      private http: HttpClient,
      protected translateService: TranslateService,
      private formChangeDetector: FormChangeDetector,
      private toastService: ToastService,
      private destroyRef: DestroyRef,
      private injector: Injector,
      public platform: Platform,
      @Inject(NULL_MEDIA) private nullMedia: Media,
    ) {
      effect(() => {
        const state = this.formState$();
        this.formState.next(state);
      })
    }
  
    ngOnInit(): void {
      const toObservableOptions = { injector: this.injector };
      const inputChange$ = combineLatest([
        toObservable(this.media$, toObservableOptions).pipe(
          filter(media => {
            return media===null || media!==undefined
          }),
          map(media => {
            return { ...this.nullMedia, ...(media || {})}
          }
        )),
        toObservable(this.editMode$, toObservableOptions).pipe(
          filter(editMode => editMode!=undefined),
          distinctUntilChanged((m1, m2) => m1==m2)
        )
      ]).pipe(
        debounceTime(100),
        switchMap(([media, editMode], index) => {
          const value: any = {
            cover: media.cover,
            name: media.name,
            author: media.author?.full_name,
            description: media.description,
            type: media.type,
            language: media.language
          };
         
          return of([media, editMode, value, index] as [Media, boolean, any, number]);
        })
      );

      const [edit$, view$] = partition(
        inputChange$,
        ([media, editMode, value, index]) => editMode
      );

      edit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(([media, editMode, value, index]) => {
          let form = this.form();
          if (!form) {
            this.form.set(form = this.createMediaForm());
          }
          const triggerChangeDetection = (value: Partial<Media>) => {
            // feeds form change detector with changes
            this.value$.set(value);
          };
          let formChangeSubscription = this.formChangeSubscription;
          if (!formChangeSubscription) {
            this.formChangeSubscription = formChangeSubscription = new Subscription();
            const detectorOptions = {
              trigger$: toObservable(this.value$, toObservableOptions).pipe(tap(value => this.logger.debug('trigger$', {value}))),
              normalizer: function(value: any) {
                return Promise.resolve({...value });
              }.bind(this),
              log: true
            };

            formChangeSubscription.add(form.statusChanges.subscribe(() => {
              this.formState$.update((state) => ({...state, valid: form.valid}));
            }));

            formChangeSubscription.add(form.valueChanges
              .subscribe(state => {
                  this.logger.debug('onValueChange', {state});
                  triggerChangeDetection(this.value);
                })
            );

            this.formChangeReset = new Subject();
            const detector = this.formChangeDetector.detect(form, {...detectorOptions, resetter: this.formChangeReset});

            formChangeSubscription.add(
              detector.subscribe((pristine: boolean) => {
                this.logger.debug('PRISTINE', pristine);
                this.formState$.update((state) => ({ ...state, dirty: !pristine }));
              })
            );

            formChangeSubscription.add(() => {
              this.formChangeSubscription = null;
              this.formChangeReset?.complete();
              this.formChangeReset = null;
              this.form.set(null);
              this.logger.debug('formChangeSubscription -> unsubscribed!');
            });
            form.reset(value);
          } else { // formChangeSubscription initialization
            this.logger.debug('RESET change detector', { value });
            form.reset(value, { onlySelf: true, emitEvent: true });
            // form.reset() effect is async (i.e. the control value is changed with a delay).
            // leverage js loop task to reset the detector state while ensuring the new form value is set 
            // and propagated to the change detector trigger$ observable.
            // We could also do: 
            // trigger$
            // .pipe(skip(1),take(1), takeUntilDestroyed(this.destroyRef))
            // .subscribe(() => this.formChangeReset?.next();)
            asyncScheduler.schedule(() => { // queueMicrotask(() => {
              this.formChangeReset?.next();
            }, 0);
          }
      });

      view$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(([media, editMode, value, index]) => {
          if (this.formChangeSubscription) {
            this.formChangeSubscription.unsubscribe();
          }
          this.value$.set(value);
        });
    }
  
    ngOnDestroy(): void {
      this.formChangeSubscription?.unsubscribe();
    }
  
    private createMediaForm() {
      return this.fb.group({
        cover: [<ImageLink | null>null],
        name: ['', [
          Validators.required,
          Validators.minLength(3),
        ]],
        description: [''],
        language: ['']
      });
    }

    private get value(): Partial<Media> {
      const {
        name,
        description,
        language
      } = this.form()?.getRawValue() || {};
    
      const mediaId = this.mediaId$();
      const media: Partial<Media> = {
        ...(mediaId ? { id: mediaId } : {}),
        name: name?.trim(),
        description: description?.trim() || undefined,
        language
      };
      return media;
    }

    // private setCoverSrc(media: Media | null) {
    //   this.value$.update(value => ({...value, media: media ?? undefined}));
    // }

    onMediaRemove(event: MouseEvent) {
      event.stopPropagation();
      this.file = null;
      // this.defaultMedia.then(media => {
      //   this.form()?.get('media')?.setValue(media);
      // })
    }
  
    onSave() {
      const state = this.formState$();
      if (state.valid && state.dirty) {
        const media = this.value$();
        if (media?.id) {
          this.save.emit(media as MediaEntity);
        }
      }
    }

    onDelete() {
      if (this.mediaId$()) {
        this.delete.emit(this.mediaId$());
      }
    }

    onCancel() {
      this.cancel.emit();
    }

    onDragOver(event: DragEvent) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = true;
    }
  
    onDrop(event: DragEvent) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = false;
      const file = event.dataTransfer?.files?.[0];
      this.handleFile(file);
    }
  
    onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      this.handleFile(input.files?.[0]);
    }
  
    handleFile(file: File | undefined) {
      if (file) {
        this.file = file;
        this.uploadError = false;
        const reader = new FileReader();
        reader.onload = () => this.upload(file, { name: this.form()?.get('name')?.value, tags: ['upload', 'media.cover'] });
        reader.readAsDataURL(file);
      }
    }
  
    upload(file: File, options?: {name?: string, tags: string[]}) {
      this.uploading.set(true);
      this.uploadProgress$.set(0);
  
      const formData = new FormData();
      formData.append('file', file);

      const name = options?.name?.trim();
      if (name) {
        formData.append('name', name);
      }

      options?.tags?.forEach(tag => {
        formData.append('tags[]', tag);
      })
  
      this.http
        .post<Media>('/media/upload', formData, {
          reportProgress: true,
          observe: 'events',
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.uploadProgress$.set(Math.round(100 * event.loaded / (event.total || 1)));
            } else if (event.type === HttpEventType.Response) {
              const media = event.body;
              if (media) {
                this.logger.debug('Media upload completed', media);
                this.form()?.patchValue(media);
              }
              setTimeout(() => {
                this.uploading.set(false);
                this.uploadProgress$.set(0);
              }, 1000);
            }
          },
          error: async (error) => {
            this.uploading.set(false);
            this.uploadError = true;
            this.toastService.error(this.translateService.translate('media.error.mediaUpload'));
          },
        });
    }

    isValid(ctrl: AbstractControl): boolean | undefined {
      return ctrl.touched && ctrl.valid ? true : ctrl.touched && ctrl.invalid ? false : undefined;
    }

    get formErrors() {
      const errors: any = {};
      const form = this.form();
      if (form) {
        Object.keys(form.controls).forEach(key => {
          const control = form.get(key);
          errors[key] = {
            value: control?.value,
            valid: control?.valid,
            errors: control?.errors
          };
        });
      }
      return errors;
    }
  }